import React, {useEffect, useMemo, useState} from "react";
import moment from "moment";
import styled, {keyframes} from "styled-components";
import {useTranslation} from "react-i18next";
import {Tag, Modal, message} from "antd";
import {
    CloseOutlined,
    ForkOutlined,
    SwapOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    SearchOutlined,
    CheckOutlined
} from "@ant-design/icons";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable/ResponsiveDataTable";
import {ConditionalTooltip} from "components/_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {CourierIcon} from "components/_defaultComponents/CourierIcon/CourierIcon";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {TextBoxFormModal} from "components/_defaultComponents/TextBoxFormModal/TextBoxFormModal";
import {TransferModal} from "components/_defaultComponents/TextBoxFormModal/TransferModal/TransferModal";
import {useStore} from "hooks/useStore";
import {deliveryStatuses} from "constants/constants";
import {getConstant, isConstantExist} from "utils/helpers";
import {
    buildOrderRows,
    isBurningQueueOrder,
    formatWaitingDuration,
    formatWaitingDurationLong
} from "../utils/orderGrouping";
import {ForceAssignCourierModal} from "./ForceAssignCourierModal";
import {IconActionButton} from "./IconActionButton";
import {ForceAssignedCourierIcon} from "./icons/ForceAssignedCourierIcon";
import {ForceAssignCourierIcon} from "./icons/ForceAssignCourierIcon";
import {TableFillArea} from "../LiveOperationPage.styles";

const BURNING_ORDER_SECONDS = 420;
const COMPANY_NAME_SHORTENER_LENGTH = 18;
const RECEIVER_NAME_SHORTENER_LENGTH = 14;
const STATUS_TAG_SHORTENER_LENGTH = 55;

// Tüm Durum tag'leri (forceAssigned, kuyrukta, teslim edildi, dinamik durumlar) bu tek stilden
// türer — hiçbiri farklı punto/satır sayısıyla öne çıkmasın diye.
const orderTagStyle = {
    height: "auto",
    minHeight: 24,
    width: 170,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 12,
    fontWeight: "normal",
    lineHeight: "13px",
    padding: "4px 8px",
    overflow: "hidden",
    whiteSpace: "normal",
    textOverflow: "ellipsis"
};

const BURNING_ORDER_COLOR = "#ce514d";

// Kod tabanında bu durumlar için hazır bir renk tanımı yoktu (regular Orders ekranı düz metin
// kullanıyor); aşamaya göre gruplanmış bir renk skalası burada tanımlandı — aynı aşamadaki
// durumlar (toplama/teslimat/iade/transfer) aynı ton ailesinden, açık zeminli antd Tag deseni
// (Kuyrukta Bekliyor / Teslim Edildi ile aynı görsel dil).
const DELIVERY_STATE_TAG_STYLE = {
    unassigned: {background: "#f5f5f5", border: "#d9d9d9", color: "rgba(0, 0, 0, 0.65)"},
    rejected: {background: "#fff1f0", border: "#ffa39e", color: "#a8071a"},
    waiting: {background: "#e6f4ff", border: "#91caff", color: "#0958d9"},
    onTheWayForPickUp: {background: "#fff7e6", border: "#ffd591", color: "#d46b08"},
    onPickUpAddress: {background: "#fff2e8", border: "#ffbb96", color: "#ad4e00"},
    onDelivery: {background: "#f9f0ff", border: "#d3adf7", color: "#531dab"},
    onDeliveryAddress: {background: "#f0e6ff", border: "#b37feb", color: "#391085"},
    onTrial: {background: "#f5f5f5", border: "#d9d9d9", color: "rgba(0, 0, 0, 0.65)"},
    onTheWayForReturn: {background: "#fff2f0", border: "#ffccc7", color: "#cf1322"},
    onReturnAddress: {background: "#fff1f0", border: "#ffa39e", color: "#a8071a"},
    waitingForTransfer: {background: "#e6fffb", border: "#87e8de", color: "#08979c"},
    onTheWayForTransfer: {background: "#e6fffb", border: "#5cdbd3", color: "#006d75"},
    onTransferAddress: {background: "#d9f7f5", border: "#36cfc9", color: "#00474f"}
};
const DEFAULT_DELIVERY_STATE_TAG_STYLE = {
    background: "#e6f4ff",
    border: "#91caff",
    color: "#0958d9"
};

// Eski Siparişler ekranındaki renderActiveDeliveryStatusText ile birebir aynı metin kaynağı
// (deliveryStatuses.<key>.name, onDeliveryAddress'te deneme kontrolü dahil) — iki ekranda aynı
// durum farklı yazmasın diye. Mergelenmiş sipariş yüzdesi (picked/delivered) burada yok: o
// hesap delivery bazlı, bu tablo satırları zaten sipariş bazlı ayrıştırılmış geliyor.
const resolveDeliveryStateName = (row, t) => {
    const delivery = row.delivery;
    if (delivery?.deliveryType === "productReturn" && delivery?.deliveryState === "returned") {
        return t("delivered.name", {ns: "deliveryStatuses"});
    }
    if (row.deliveryState === "onDeliveryAddress") {
        const endDate = delivery?.steps?.slice(-1)[0]?.endDate;
        const stepIndex =
            endDate && ["delivered", "returned"].includes(delivery?.deliveryState)
                ? delivery.currentStepIndex + 1
                : delivery.currentStepIndex - 1;
        const currentStep = delivery?.steps?.[stepIndex];
        if (currentStep?.trial?.startDate && !currentStep?.trial?.endDate) {
            return t("onTrial.name", {ns: "deliveryStatuses"});
        }
    }
    if (isConstantExist(deliveryStatuses, row.deliveryState)) {
        return t(`${getConstant(deliveryStatuses, row.deliveryState).translation_key}.name`, {
            ns: "deliveryStatuses"
        });
    }
    return "Kurye Atandı";
};

const deliveryStateTag = (row, t) => {
    if (row.statusKey === "forceAssigned") {
        return (
            <Tag
                style={{
                    ...orderTagStyle,
                    flexDirection: "column",
                    gap: 2,
                    background: "#fff7e6",
                    borderColor: "#ffd591",
                    color: "#d46b08"
                }}
            >
                <span style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <Shortener text="Atama Bekleniyor" length={STATUS_TAG_SHORTENER_LENGTH} />
                </span>
                <Shortener
                    text={row.forceCourier?.fullname || ""}
                    length={STATUS_TAG_SHORTENER_LENGTH}
                    render={shortText => <span style={{fontWeight: 400}}>{shortText}</span>}
                />
            </Tag>
        );
    }
    if (!row.courierId) {
        return (
            <Tag
                style={{
                    ...orderTagStyle,
                    background: "#fff7e6",
                    borderColor: "#ffd591",
                    color: "#d46b08"
                }}
            >
                <Shortener text="Kuyrukta Bekliyor" length={STATUS_TAG_SHORTENER_LENGTH} />
            </Tag>
        );
    }
    if (row.deliveryState === "delivered") {
        return (
            <Tag
                style={{
                    ...orderTagStyle,
                    background: "#f5f5f5",
                    borderColor: "#d9d9d9",
                    color: "rgba(0, 0, 0, 0.65)"
                }}
            >
                <Shortener text="Teslim Edildi" length={STATUS_TAG_SHORTENER_LENGTH} />
            </Tag>
        );
    }
    const style = DELIVERY_STATE_TAG_STYLE[row.deliveryState] || DEFAULT_DELIVERY_STATE_TAG_STYLE;
    const label = resolveDeliveryStateName(row, t);
    return (
        <Tag
            style={{
                ...orderTagStyle,
                background: style.background,
                borderColor: style.border,
                color: style.color
            }}
        >
            <Shortener text={label} length={STATUS_TAG_SHORTENER_LENGTH} />
        </Tag>
    );
};

const pulseRing = keyframes`
    0% {
        transform: scale(1);
        opacity: 0.5;
    }
    100% {
        transform: scale(1.6);
        opacity: 0;
    }
`;

const PulsingClockBadge = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-right: 8px;
    flex: none;
    vertical-align: middle;
    border-radius: 50%;
    background: #faad141a;

    &::before,
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: 50%;
        border: 1.5px solid #faad14;
        opacity: 0;
        animation: ${pulseRing} 2.4s ease-out infinite;
    }

    &::after {
        animation-delay: 1.2s;
    }

    .anticon {
        position: relative;
        z-index: 1;
        font-size: 14px;
        color: #faad14;
    }
`;

const OrderNumberCell = styled.span`
    position: relative;
    cursor: pointer;
`;

const OrderNumberCopyTick = styled.span`
    position: absolute;
    top: 50%;
    left: -16px;
    transform: translateY(-50%);
    display: flex;
    color: #52c41a;
    font-size: 12px;
`;

const CourierNameLink = styled.a`
    color: inherit;

    &:hover {
        color: #1890ff;
    }
`;

const GroupCell = ({row}) => {
    if (row.groupType === "queue") {
        return (
            <span>
                <PulsingClockBadge>
                    <ClockCircleOutlined />
                </PulsingClockBadge>
                <strong>Atama Bekliyor</strong> ({row.count})
            </span>
        );
    }
    return (
        <span>
            <CourierIcon courierType={row.courierType || "motorCourier"} />{" "}
            {/* Ad kurye detayına gider, satırın geri kalanı haritada odaklar — ikisi ayrı
                aksiyon olduğu için tıklama burada durdurulur. */}
            <CourierNameLink
                href={`/couriers/${row.courierId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={event => event.stopPropagation()}
                style={{marginLeft: 6}}
            >
                <strong>{row.courierName}</strong>
            </CourierNameLink>{" "}
            ({row.count})
        </span>
    );
};

export const ActiveOrdersTable = ({
    queueData,
    currentData,
    onReload,
    onCountChange,
    statusFilter,
    courierSearchText,
    focusedKey = null,
    onFocusChange
}) => {
    const stores = useStore();
    const {t} = useTranslation(["deliveryStatuses"]);
    const [assignTarget, setAssignTarget] = useState(null);
    const [removeForceTarget, setRemoveForceTarget] = useState(null);
    const [transferDeliveryId, setTransferDeliveryId] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [splitTarget, setSplitTarget] = useState(null);
    const [copiedRowKey, setCopiedRowKey] = useState(null);

    // Sipariş numarasına tıklama satırın kendi onClick'inin (haritada odaklama) İÇİNDE oluyor;
    // stopPropagation olmadan tıklama hem kopyalar hem de satırı odaklardı. `navigator.clipboard`
    // yalnızca güvenli bağlamda (https/localhost) çalışır; `execCommand` fallback'i
    // WhatsappSupportButton'daki kalıptan alındı.
    const handleCopyOrderNumber = (event, orderNumber, rowKey) => {
        event.stopPropagation();
        const onCopied = () => {
            // Kopyalanma zaten sipariş numarasının yanındaki tikle gösteriliyor; mesajda
            // ikinci bir tik ikonuna gerek yok — `icon` boş verilerek varsayılanı kapatılır.
            message.open({content: `${orderNumber} kopyalandı`, type: "success", icon: <span />});
            setCopiedRowKey(rowKey);
            setTimeout(
                () => setCopiedRowKey(current => (current === rowKey ? null : current)),
                2000
            );
        };
        try {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(orderNumber).then(onCopied);
            } else {
                const el = document.createElement("textarea");
                el.value = orderNumber;
                el.style.position = "fixed";
                el.style.opacity = "0";
                document.body.appendChild(el);
                el.focus();
                el.select();
                document.execCommand("copy");
                document.body.removeChild(el);
                onCopied();
            }
        } catch (e) {
            message.error("Sipariş numarası kopyalanamadı");
        }
    };

    // Aksiyonların rol kapıları, bu aksiyonların eskiden yaşadığı ekranlardan birebir alınır:
    // Zorunlu Kurye Ata / Atamayı Kaldır ile Siparişi İptal Et için sipariş listesi
    // (components/order/OrderTable), Siparişi Ayır ve Transfer Et için sipariş detayı
    // (components/order/orderDetail/OrderDetail). Sayfanın kendi AuthChecker rol listesi bunları
    // kapsamıyor: ör. fieldManager ve sOperation sayfaya girebiliyor ama eski ekranlarda bu
    // aksiyonların hiçbirini alamıyordu.
    // Zorunlu kurye atama, sipariş iptali ve sipariş ayırma tek bir "Sipariş İşlemleri"
    // yetkisinde birleştirildi (bkz. constants/pagePermissions.js).
    const canForceAssign =
        stores.meStore.checkRole("updateForceCourier") &&
        stores.meStore.checkPermission("liveOperation.orderActions");
    const canCancelOrder =
        stores.meStore.checkRole("cancelRepeatOrder") &&
        stores.meStore.checkPermission("liveOperation.orderActions");
    const canTransferOrder = stores.meStore.checkRole("transferOrder");
    // Siparişi Ayır'ın OrderDetail'deki kuralı checkRole'a taşınmamış: sabit rol listesi ya da
    // kullanıcının dedike çalışan bir şubesi olması yetiyor. Aynısı burada tekrarlanıyor.
    const canSplitOrder =
        (["admin", "oUser", "superadmin", "bm", "sy", "agency", "sChef"].includes(
            stores.meStore.role
        ) ||
            (stores.meStore.subcompanies || []).some(
                subcompany => subcompany.subCompanyWorkType === "dedicated"
            )) &&
        stores.meStore.checkPermission("liveOperation.orderActions");

    const rows = useMemo(
        () =>
            buildOrderRows({
                queueDeliveries: queueData?.rows,
                currentDeliveries: currentData?.rows,
                statusFilter,
                courierSearchText
            }),
        [queueData?.rows, currentData?.rows, statusFilter, courierSearchText]
    );

    const totalOrders = (queueData?.dataCount || 0) + (currentData?.dataCount || 0);

    useEffect(() => {
        onCountChange?.(totalOrders);
    }, [totalOrders]);

    const reload = () => {
        setAssignTarget(null);
        setRemoveForceTarget(null);
        setTransferDeliveryId(null);
        setCancelTarget(null);
        setSplitTarget(null);
        onReload?.();
    };

    const handleRemoveForceConfirm = async () => {
        await stores.orderStore.removeForceCourier({deliveryId: removeForceTarget?.deliveryId});
        reload();
    };

    const columns = [
        {
            title: "Firma - Şube",
            dataIndex: "companyName",
            width: 150,
            onCell: () => ({"data-label": "Firma - Şube"}),
            render: (value, row) => {
                if (row._rowType === "group") {
                    return {children: <GroupCell row={row} />, props: {colSpan: 5}};
                }
                const burning = !row.courierId && isBurningQueueOrder(row, BURNING_ORDER_SECONDS);
                return (
                    <>
                        <Shortener
                            text={value}
                            length={COMPANY_NAME_SHORTENER_LENGTH}
                            render={shortText =>
                                burning ? (
                                    <span style={{color: BURNING_ORDER_COLOR, fontWeight: 500}}>
                                        <ExclamationCircleOutlined style={{marginRight: 6}} />
                                        {shortText}
                                    </span>
                                ) : (
                                    <span style={{fontWeight: 500}}>{shortText}</span>
                                )
                            }
                        />
                        <Shortener
                            text={row.subCompanyName}
                            length={COMPANY_NAME_SHORTENER_LENGTH}
                            render={shortText => (
                                <div style={{fontSize: 12, color: "rgba(0, 0, 0, 0.45)"}}>
                                    {shortText}
                                </div>
                            )}
                        />
                    </>
                );
            }
        },
        {
            title: "Sipariş No",
            dataIndex: "orderNumber",
            width: 100,
            onCell: () => ({"data-label": "Sipariş Numarası"}),
            render: (value, row) =>
                row._rowType === "group" ? (
                    {props: {colSpan: 0}}
                ) : (
                    <ConditionalTooltip
                        condition
                        text="Kopyala"
                        child={
                            <OrderNumberCell
                                style={{fontWeight: 400}}
                                onClick={event => handleCopyOrderNumber(event, value, row.key)}
                                data-testid="orderNumberCopy"
                            >
                                {value}
                                {copiedRowKey === row.key && (
                                    <OrderNumberCopyTick>
                                        <CheckOutlined />
                                    </OrderNumberCopyTick>
                                )}
                            </OrderNumberCell>
                        }
                    />
                )
        },
        {
            title: "Müşteri",
            dataIndex: "receiverName",
            width: 100,
            onCell: () => ({"data-label": "Müşteri"}),
            render: (value, row) =>
                row._rowType === "group" ? (
                    {props: {colSpan: 0}}
                ) : (
                    <Shortener
                        text={value}
                        length={RECEIVER_NAME_SHORTENER_LENGTH}
                        render={shortText => <span style={{fontWeight: 400}}>{shortText}</span>}
                    />
                )
        },
        {
            title: "Oluşturulma Zamanı",
            dataIndex: "createdAt",
            width: 130,
            onCell: () => ({"data-label": "Oluşturulma Zamanı"}),
            render: (value, row) => {
                if (row._rowType === "group") return {props: {colSpan: 0}};
                const timeCell = (
                    <span
                        style={{
                            color: "rgba(0, 0, 0, 0.45)",
                            whiteSpace: "nowrap",
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        {value ? moment(value).format("HH:mm") : "-"}
                        {value && (
                            <text
                                style={{marginLeft: 2, fontSize: 10, color: "rgba(0, 0, 0, 0.45)"}}
                            >
                                ({formatWaitingDuration(row.createdAt)})
                            </text>
                        )}
                    </span>
                );
                if (!value) return timeCell;
                return (
                    <ConditionalTooltip
                        condition
                        text={
                            <>
                                {moment(value).format("DD.MM.YYYY HH:mm")}
                                <br />({formatWaitingDurationLong(row.createdAt)})
                            </>
                        }
                        child={timeCell}
                    />
                );
            }
        },
        {
            title: "Durum",
            dataIndex: "deliveryState",
            width: 190,
            onCell: () => ({"data-label": "Durum"}),
            render: (value, row) =>
                row._rowType === "group" ? {props: {colSpan: 0}} : deliveryStateTag(row, t)
        },
        {
            title: "İşlemler",
            key: "actions",
            width: 130,
            align: "right",
            onCell: () => ({"data-label": "İşlemler"}),
            render: (_, row) => {
                if (row._rowType === "group") {
                    const showSplit = Boolean(row.splitDeliveryId) && canSplitOrder;
                    const showTransfer = Boolean(row.transferDeliveryId) && canTransferOrder;
                    // Kuryenin tek (mergelenmiş ya da tekil) delivery'si varsa detay butonu
                    // gösterilir; birden fazla AYRI delivery taşıyorsa Split/Transfer'daki gibi
                    // hangisinin kastedildiği belirsiz olduğundan gizlenir.
                    const showDetail = Boolean(row.transferDeliveryId);
                    const hasAnyAction = showSplit || showTransfer || showDetail;
                    if (row.groupType !== "courier" || !hasAnyAction) {
                        return null;
                    }
                    return {
                        children: (
                            <span onClick={event => event.stopPropagation()}>
                                {showDetail && (
                                    <ConditionalTooltip
                                        condition
                                        text="Sipariş Detayı"
                                        child={
                                            <IconActionButton
                                                $color="#595959"
                                                icon={<SearchOutlined />}
                                                onClick={() =>
                                                    window.open(
                                                        `/orders/${row.transferDeliveryId}`,
                                                        "_blank"
                                                    )
                                                }
                                                data-testid="orderGroupDetailButton"
                                            />
                                        }
                                    />
                                )}
                                {showSplit && (
                                    <ConditionalTooltip
                                        condition
                                        text="Siparişi Ayır"
                                        child={
                                            <IconActionButton
                                                $color="#d46b08"
                                                icon={<ForkOutlined />}
                                                onClick={() => setSplitTarget(row)}
                                                data-testid="splitOrderGroupButton"
                                            />
                                        }
                                    />
                                )}
                                {showTransfer && (
                                    <ConditionalTooltip
                                        condition
                                        text="Transfer Et"
                                        child={
                                            <IconActionButton
                                                $color="#1890ff"
                                                icon={<SwapOutlined />}
                                                onClick={() =>
                                                    setTransferDeliveryId(row.transferDeliveryId)
                                                }
                                                data-testid="transferGroupButton"
                                            />
                                        }
                                    />
                                )}
                            </span>
                        )
                    };
                }

                // Eski sipariş ekranlarında iptal/ayır/transfer butonları teslim edilmiş, iptal
                // edilmiş ve iade edilmiş siparişlerde hiç gösterilmiyordu. Burada gizlemek
                // yerine pasif bırakılıyor (bu sayfanın geneliyle tutarlı olsun diye), sebebi de
                // tooltip'e yazılıyor.
                const closedState = ["delivered", "canceled", "returned"].includes(
                    row.deliveryState
                );
                const cancelDisabled = closedState;

                return (
                    <span onClick={event => event.stopPropagation()}>
                        {row.statusKey === "queue" && canForceAssign && (
                            <ConditionalTooltip
                                condition
                                text="Zorunlu Kurye Ata"
                                child={
                                    <IconActionButton
                                        $color="#d46b08"
                                        icon={<ForceAssignCourierIcon />}
                                        onClick={() => setAssignTarget(row.delivery)}
                                        data-testid="forceAssignCourierButton"
                                    />
                                }
                            />
                        )}
                        {row.statusKey === "forceAssigned" && canForceAssign && (
                            <ConditionalTooltip
                                condition
                                text="Zorunlu Kurye Atamasını Kaldır"
                                child={
                                    <IconActionButton
                                        $color="#d46b08"
                                        icon={<ForceAssignedCourierIcon />}
                                        onClick={() => setRemoveForceTarget(row)}
                                        data-testid="removeForceCourierButton"
                                    />
                                }
                            />
                        )}
                        {canCancelOrder && (
                            <ConditionalTooltip
                                condition
                                text={
                                    cancelDisabled
                                        ? "Sipariş kapandığı için iptal edilemez."
                                        : "Siparişi İptal Et"
                                }
                                child={
                                    <IconActionButton
                                        $color="#f5222d"
                                        icon={<CloseOutlined />}
                                        disabled={cancelDisabled}
                                        onClick={() => {
                                            if (cancelDisabled) return;
                                            setCancelTarget(row);
                                        }}
                                        data-testid="cancelOrderButton"
                                    />
                                }
                            />
                        )}
                    </span>
                );
            }
        }
    ];

    return (
        <>
            <TableFillArea>
                <ResponsiveDataTable
                    className="live-operation-fill-table"
                    data-testid="activeOrdersTable"
                    tableLayout="fixed"
                    scroll={{x: 800}}
                    columns={columns}
                    dataSource={rows}
                    rowKey="key"
                    size="small"
                    pagination={false}
                    rowClassName={row =>
                        row._rowType === "group"
                            ? row.groupType === "queue"
                                ? "live-operation-group-row live-operation-group-row--queue"
                                : "live-operation-group-row live-operation-group-row--courier"
                            : !row.courierId && isBurningQueueOrder(row, BURNING_ORDER_SECONDS)
                            ? "live-operation-burning-row"
                            : ""
                    }
                    onRow={row => {
                        if (row._rowType === "group") {
                            // Kurye grubuna tıklama artık kurye detayına gitmez, sipariş
                            // satırlarındaki gibi haritada odaklar (kurye + tüm siparişleri +
                            // ilgili şubeler); detaya gidiş yalnız GroupCell'deki isim linkiyle.
                            return row.groupType === "courier" && row.courierId
                                ? {
                                      onClick: () =>
                                          onFocusChange(
                                              // Aynı satıra ikinci tık odağı kapatır.
                                              focusedKey === row.key
                                                  ? null
                                                  : {
                                                        type: "courier",
                                                        key: row.key,
                                                        courierId: row.courierId,
                                                        courierName: row.courierName
                                                    }
                                          ),
                                      style: {cursor: "pointer"}
                                  }
                                : {};
                        }
                        return {
                            onClick: () =>
                                onFocusChange(
                                    // Aynı satıra ikinci tık odağı kapatır.
                                    focusedKey === row.key
                                        ? null
                                        : {
                                              type: "order",
                                              key: row.key,
                                              deliveryId: row.deliveryId,
                                              orderId: row.orderId,
                                              orderNumber: row.orderNumber
                                          }
                                ),
                            style: {cursor: "pointer"}
                        };
                    }}
                />
            </TableFillArea>

            <ForceAssignCourierModal
                isVisible={Boolean(assignTarget)}
                delivery={assignTarget}
                onClose={() => setAssignTarget(null)}
                onAssigned={reload}
            />

            <Modal
                title="Zorunlu Kurye Atamasını Kaldır"
                visible={Boolean(removeForceTarget)}
                onOk={handleRemoveForceConfirm}
                onCancel={() => setRemoveForceTarget(null)}
                okText="Zorunlu Kurye Atamasını Kaldır"
                cancelText="İptal Et"
            >
                <p>
                    İlgili siparişte zorunlu kurye atamasını kaldırdığınızda, sipariş müsait
                    durumdaki herhangi bir kuryeye atanabilir.
                </p>
            </Modal>

            {transferDeliveryId && (
                <TransferModal
                    deliveryId={transferDeliveryId}
                    isVisible
                    setVisible={reload}
                    cancelFn={() => setTransferDeliveryId(null)}
                />
            )}

            <TextBoxFormModal
                isVisible={Boolean(cancelTarget)}
                setVisible={() => setCancelTarget(null)}
                cancelFn={() => setCancelTarget(null)}
                deliveryId={cancelTarget?.deliveryId}
                modalType="cancelOrder"
                modalData={{deliveryId: cancelTarget?.deliveryId, orderId: cancelTarget?.orderId}}
                label="Sipariş iptal sebebi"
                title="Sipariş İptal"
                okFunction={async params => {
                    await stores.orderStore.cancelOrder(params);
                    reload();
                }}
            />

            <TextBoxFormModal
                isVisible={Boolean(splitTarget)}
                setVisible={() => setSplitTarget(null)}
                cancelFn={() => setSplitTarget(null)}
                deliveryId={splitTarget?.splitDeliveryId}
                deliveryData={{delivery: splitTarget?.splitDelivery}}
                modalType="splitOrder"
                modalData={{
                    deliveryId: splitTarget?.splitDeliveryId,
                    orderSteps: (splitTarget?.splitDelivery?.orders || []).map(order => ({
                        stepInfo: order
                    }))
                }}
                label="Ayrılacak siparişler"
                title="Siparişi Ayır"
                allowMergeDefault
                allPoolCouriers
                okFunction={async params => {
                    await stores.orderStore.splitOrder(params);
                    reload();
                }}
            />
        </>
    );
};
